/**
 * Palette of new card elements, which are small cards that is used to open attribute and case card in edit mode.
 */
export class ElemNewCard {

    constructor( rect, playContainer, type = 'attribute' ) {
        this.type = type; // 'attribute' or 'case'
        this.cardCorner = 8;
        this.tipYOffset = 0.9;     // fraction of height from top
        this.tipXOffset = 1.0;      // fraction of width from left

        this.cardOutlineDash = '3,3';
        this.storkeColor = type === 'attribute'? "#A71931": "#1D4CBC";
        this.cardBackFill = type === 'attribute'? "#FBEAEF": "#E6E9F7";
        this.cardNumFontSize = 14;

        let cardBack = ElemNewCard.newCardBack( playContainer, rect , this );
        this.back = cardBack; // card number and icon SVG element
        this.showHideCard( playContainer.options.isIconOnly, playContainer.Dyn );
        // add cross-references
        cardBack.data("card", this );
    }

    destroy() {
        // remove raphael elements from paper before destroying
        this.back.remove();
        delete this.back;
    }

    /**
     * Create a new small card back, which has a round-rect, icon, and card no., and is draggable.
     * And it has an icon-only state, where the round-rect and card no are hidden.
     * Caller must set the icon-only state. Here we create it in the isIconOnly===false state.
     * @param playContainer -- container with has SVG paper to add to
     * @param iconStyler - styler which holds custom icon, if any
     * @param caseIndex - case index of this card, in dataset
     * @param smallCardIndex - card index, different from case index if cases are filtered out.
     * @param bbox - position of card back
     * @returns {*}
     */
    static newCardBack( playContainer, bbox, newCard ) {
        let Dyn = playContainer.Dyn,
            paper = playContainer.paper;

            let label = "";
            switch(newCard.type){
                case 'attribute':
                    label = "Attribute";
                    break;
                case 'attributeopen':
                    label = "Attribute Opened";
                    break;
                case 'case':
                    label = "Case";
                    break;
            }

        paper.setStart();
        let rrect = createRectEl( paper, bbox ),
            textEl = createTextEl( paper, label),
            iconEl = createIconEl( paper, bbox );
        let set = paper.setFinish();
        set.getRectEl = () => { return rrect; }
        set.getIconEl = () => { return iconEl; }
        set.getTextEl = () => { return textEl; }
        set.translate( bbox.x, bbox.y ); // use transform to move in paper coordinates
        return set;

        function createRectEl( paper, bbox ) {
            let rrect = paper.rect( 0, 0, bbox.width, bbox.height, newCard.cardCorner )
                    .attr({
                        'stroke':newCard.storkeColor,
                        'stroke-width':2,
                        'fill': newCard.cardBackFill,
                    });
                    // rrect.node.setAttribute('stroke-dasharray', newCard.cardOutlineDash );
            setClickEventListener( rrect );
            return rrect;
        }

        function createTextEl( paper, textStr ) {
            // text and icon are not draggable, except icon is draggable when isIconOnly===true
            const textYOffset = 0.8;
            const textY = bbox.height * textYOffset;
            let textEl = paper.text( bbox.width*0.5, textY, textStr)
                    .attr({"font-size":newCard.cardNumFontSize, 'fill': newCard.storkeColor});
            Dyn.plotUtil.disableMouseOverEvents( textEl );
            return textEl;
        }

        function createIconEl( paper, bbox ) {
            const iconY = bbox.height*0.4,
                iconX = bbox.width/2,
                pathStr = `M${iconX} ${iconY-8}v16M${iconX-8} ${iconY}h16`;

            let iconEl = paper.path( pathStr )
                    .attr({
                        'stroke': newCard.storkeColor,
                        'stroke-width': 4,
                        'stroke-linecap': "round",
                        'stroke-linejoin': "round"
                    });
            Dyn.plotUtil.disableMouseOverEvents( iconEl );
            return iconEl;
        }

        function setClickEventListener( elem ) {
            elem.data("tabIndex", 0 ) // NOTE: used to set/restore tabindex after enable/disable
                .hover( hoverIn, hoverOut );
            elem.node.onfocus = focusHandler; // on focus, set aria-label, show tooltip, etc
            elem.node.onblur = blurHandler;
            elem.click(function(){
                const isAttributeCard = newCard.type === 'attribute';
                if(isAttributeCard) {
                    // playContainer.editableView.hideAttributeView();
                    playContainer.setEditModeOn();
                } else {
                    // Add new case in casecard view
                    playContainer.editableView.cardView.addCase();
                }
            });
            Dyn.plotUtil.setKeyboardFocusable( elem, keyDownHandler.bind( elem ) );
            elem.getSet = () => { return set; }
        }

        function showToolTip( set ) {
            let rrect = set.getRectEl(),
                bbox = rrect.getBBox();
            rrect.data("tooltip", newCard.type === 'attribute'? "Add new Attribute": "Add new Case");
            Dyn.toolTip.show( rrect, event, {
                x: bbox.x + bbox.width*newCard.tipXOffset,
                y: bbox.y + bbox.height*newCard.tipYOffset
            } );
        }

        function hideToolTip( Dyn, set ) {
            let rrect = set.getRectEl();
            rrect.data( { "tooltip": "" });
            if( Dyn.toolTip.isMyElement( rrect ))
                Dyn.toolTip.hide();
        }

        function keyDownHandler( event ) {
            // this === rrect OR iconEl
            switch( event.key ) { // const {key} = event; ES6+
                case "Enter":
                    showToolTip( set );
                    break;
                case "Escape":
                    hideToolTip( Dyn, set );
                    break;
            }
        }

        // keyboard focus
        function focusHandler( event ) {
            let rrect = set.getRectEl(),
                iconEl = set.getIconEl();
            // update and show tool tip on keyboard focus
            showToolTip( set );

            // aria-label of the visible element
            if( Dyn.plotUtil.isElementVisible(rrect))
                rrect.node.setAttribute( "aria-label", rrect.data("tooltip") );
            if( Dyn.plotUtil.isElementVisible(iconEl))
                iconEl.node.setAttribute( "aria-label", rrect.data("tooltip") );
        }

        // when keyboard focus leaves this icon
        function blurHandler( event ) {
            //let set = this.getSet(); // this === rrect OR icon
            hideToolTip( Dyn, set );
        }
        function hoverIn( event ) { // called with hoverIn event, or dragStart/dragMove on touch devices
            let set = this.getSet(), // this === rrect OR icon
                rrect = set.getRectEl(),
                iconEl = set.getIconEl(),
                wasFocus = Dyn.plotUtil.isKeyboardFocus( this );
            set.toFront();

            playContainer.home.addGlow(rrect);

            showToolTip( set );
            if( wasFocus ) {
                Dyn.plotUtil.setAsKeyboardFocus( this );
            }
        }
        function hoverOut( event ) { // called with hoverOut event, or dragEnd on touch devices
            let set = this.getSet(), // this === rrect OR icon
                rrect = set.getRectEl(),
                wasFocus = Dyn.plotUtil.isKeyboardFocus( this );
            playContainer.home.removeGlow(); // remove glow if any
            hideToolTip( Dyn, set );
            playContainer.layerElements();
            if( wasFocus ) {
                Dyn.plotUtil.setAsKeyboardFocus( this );
            }
        }

    }

    /**
     * Update the visibility of the card, with 3 states:
     * (1) Hide the card entirely (if isMissing==true)
     * or (2) show the card Back (outline+number+icon)
     * or (3) show the icon only (if isIconOnly==true)
     * @param isIconOnly
     * @param Dyn
     */
    showHideCard( isIconOnly, Dyn ) {
        // console.log("showHideCard", this.smallCardIndex+1, this );
        let rectEl = this.back.getRectEl(),
            textEl = this.back.getTextEl(),
            iconEl = this.back.getIconEl();
            //isMissing = rectEl.data("isMissing");
        if( this.isMissing || this.isBig ) { // hide entire card if in plot and missing or in big card
            hide( rectEl );
            hide( textEl );
            hide( iconEl );
        } else if( isIconOnly ) {
            hide( rectEl );
            hide( textEl );
            show( iconEl, true, iconEl.data("tabIndex"));
            Dyn.plotUtil.enableMouseOverEvents( iconEl );
        } else { // not missing and not icon-only
            show( rectEl, true, rectEl.data("tabIndex"));
            show( textEl, false );
            show( iconEl, false );
        }
        function hide( elem ) {
            elem.hide();
            Dyn.plotUtil.disableMouseOverEvents( elem );
            Dyn.plotUtil.disableKeyboardFocus( elem );
            elem.node.setAttribute( "tabindex", -1 );
        }
        function show( elem, canMouseAndKeyboard, tabIndex ) {
            elem.show();
            if( canMouseAndKeyboard ) {
                Dyn.plotUtil.enableMouseOverEvents( elem );
                Dyn.plotUtil.enableKeyboardFocus( elem );
                elem.node.setAttribute( "tabindex", tabIndex );
            } else {
                Dyn.plotUtil.disableMouseOverEvents( elem );
                Dyn.plotUtil.disableKeyboardFocus( elem );
                elem.node.setAttribute( "tabindex", -1 );
            }
        }
    }

    animateCardTo( transform, speed, iconFillColor=null, handleAnimationEnd=null ) {
        this.back.animate({ transform:transform }, speed.time, speed.easing, handleAnimationEnd );
    }

    reposition( rect ) {
        this.animateCardTo( "t"+rect.x+","+rect.y, 0.5 );
    }
}
